import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";

import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  Event as RouterEvent,
} from "@angular/router";
import { Subscription, filter } from "rxjs";
import { AuthService } from "src/app/features/auth/services/auth.service";
import { PersonalResponse } from "./../../../features/profile/models/personal-dashboard";
import { PersonalDashboardServiceService } from "src/app/features/profile/services/personal-dashboard-service.service";
import { AffiliateService } from "src/app/features/affiliate/services/affiliate.service";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";
import { CommonDialogData } from "../../models/dialog.interface";
import { CommonDialogComponent } from "../common-dialog/common-dialog.component";
import { InformationDialogComponent } from "src/app/shared/components/information-dialog/information-dialog.component";
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from 'src/app/core/services/translation.service';
import { AnalyticsService } from "src/app/core/services/analytics.service";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();
  path: string = "/";
  userId = localStorage.getItem("userId");
  isMenuOpen = false;
  isLoggingOut = false;
  personalData: PersonalResponse | null = null;
  mobileMenuOpen = false;
  isScrolled: boolean = false;
  isAffiliate = false;
  isPendingCancellation = false;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.isScrolled && window.scrollY < 10) {
      this.isScrolled = false;
    } else if (!this.isScrolled && window.scrollY > 150) {
      this.isScrolled = true;
    }
  }

  private routeSub!: Subscription;

  constructor(
    private router: Router,
    public authService: AuthService,
    private personalDashboardService: PersonalDashboardServiceService,
    private matDialog: MatDialog,
    private destroyRef: DestroyRef,
    private cdref: ChangeDetectorRef,
    private route: ActivatedRoute,
    public translate: TranslateService,
    public translationService: TranslationService,
    private analytics: AnalyticsService,
    private affiliateService: AffiliateService
  ) {
    this.router.events
      .pipe(
        filter(
          (event: RouterEvent): event is NavigationEnd =>
            event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (event: NavigationEnd) => {
          this.path = event.urlAfterRedirects;
          this.mobileMenuOpen = false;
        },
      });
  }

  changeLang(lang: string) {
    this.translationService.setLanguage(lang);
  }

  ngOnInit(): void {
    this.fetchUserData();
    this.fetchAffiliateStatus();

    this.routeSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.cdref.detectChanges();
      });
  }

  fetchUserData() {
    if (!this.userId) {
      return;
    }

    this.subscription.add(
      this.personalDashboardService.getPersonalData(this.userId).subscribe({
        next: (res) => {
          this.personalData = res?.data;
        },
        error: (err) => {
          console.log(err);
        },
      }),
    );
  }

  fetchAffiliateStatus() {
    if (!this.userId) return;
    // Call API once to initialize
    this.subscription.add(
      this.affiliateService.getDashboard().subscribe({
        // The service resets affiliate status; the interceptor reports unexpected errors.
        error: () => {},
      })
    );
    
    // Subscribe to state changes so it updates immediately when other components fetch it
    this.subscription.add(
      this.affiliateService.isAffiliate$.subscribe(status => {
         this.isAffiliate = status;
         this.cdref.markForCheck();
      })
    );
    this.subscription.add(
      this.affiliateService.isPendingCancellation$.subscribe(status => {
         this.isPendingCancellation = status;
         this.cdref.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.routeSub) this.routeSub.unsubscribe();
  }

  logout(): void {
    this.isMenuOpen = false;
    this.isLoggingOut = true;

    this.analytics.setUserId(null);

    this.authService.logoutApi().subscribe({
      next: () => this.executeLocalLogout(),
      error: () => this.executeLocalLogout()
    });
  }

  private executeLocalLogout(): void {
    const currentLang = localStorage.getItem('user_lang');

    this.authService.removeToken();
    localStorage.clear();

    if (currentLang) {
      localStorage.setItem('user_lang', currentLang);
    }

    this.userId = null;
    this.personalData = null;
    this.matDialog.closeAll();

    this.matDialog.open(InformationDialogComponent, {
      width: '400px',
      data: {
        title: 'Thành công',
        message: this.translate.instant('common.HEADER.LOGOUT_SUCCESS'),
        type: 'success'
      }
    });

    setTimeout(() => {
      const returnUrl = window.location.origin;
      window.location.href = `${environment.normalUserPageUrl}/auth/login?action=logout&returnUrl=${encodeURIComponent(returnUrl)}`;
    }, 1000);
  }

  closeMenu(): void {
    this.mobileMenuOpen = false;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  onProfileClick(): void {
    if (this.isLoggingOut) return;
    this.closeMenu();
    this.authService.isLoggedIn().subscribe((loggedIn) => {
      if (loggedIn) {
        this.router.navigate(["/profiles"]);
      } else {
        this.matDialog.open(CommonDialogComponent, {
          width: "400px",
          data: {
            title: this.translate.instant('common.HEADER.NOTIFICATION'),
            content: this.translate.instant('common.HEADER.LOGIN_REQUIRED'),
            redirectTo: "auth/login",
            loadSpecificContent: "text",
          } as CommonDialogData,
        });
      }
    });
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    if (!this.isMenuOpen) return;
    const side = document.querySelector(".side-menu") as HTMLElement | null;
    const toggle = document.querySelector(".menu-toggle") as HTMLElement | null;
    const target = event.target as HTMLElement;

    if (side && side.contains(target)) return;
    if (toggle && toggle.contains(target)) return;
    this.closeMenu();
  }

  isCancelModalVisible = false;
  isCanceling = false;

  openCancelModal() {
    if (this.isPendingCancellation) {
       this.matDialog.open(InformationDialogComponent, {
          width: '400px',
          data: { title: 'Thông báo', message: 'Yêu cầu huỷ đăng ký của bạn đang đợi duyệt.', type: 'info' }
       });
       return;
    }
    this.isCancelModalVisible = true;
  }

  closeCancelModal() {
    this.isCancelModalVisible = false;
  }

  executeCancel() {
    this.isCanceling = true;
    this.subscription.add(
      this.affiliateService.cancel().subscribe({
        next: (res) => {
          if (res.success) {
            this.matDialog.open(InformationDialogComponent, {
              width: '400px',
              data: { title: 'Thành công', message: 'Đã hủy đăng ký Affiliate', type: 'success' }
            });
            this.router.navigate(['/']);
          }
          this.isCanceling = false;
          this.closeCancelModal();
          this.cdref.markForCheck();
        },
        error: (err) => {
          this.matDialog.open(InformationDialogComponent, {
            width: '400px',
            data: { title: 'Lỗi', message: err?.error?.message || 'Lỗi khi hủy đăng ký', type: 'error' }
          });
          this.isCanceling = false;
          this.closeCancelModal();
          this.cdref.markForCheck();
        }
      })
    );
  }
}
